import { Inject, Injectable, OnDestroy } from '@angular/core';
import {
  ActivityInterface,
  CrmObjectInterface,
  FieldSchemaInterface,
  SortByInterface,
  SortDirection,
} from '@vendasta/crm';
import {
  GalaxyFilterDefinitionInterface,
  GalaxyFilterInterface,
  GalaxyFilterOperator,
} from '@vendasta/galaxy/filter/chips';
import { CustomCell, GalaxyColumnDef } from '@vendasta/galaxy/table';
import {
  Observable,
  ReplaySubject,
  combineLatest,
  firstValueFrom,
  of,
  BehaviorSubject,
  Subject,
  takeUntil,
} from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ActivityTaskStatus,
  TaskStatusCellComponent,
  TaskBodyCellComponent,
  AssociatedCrmObjectComponent,
  CrmInjectionToken,
  CrmDependencies,
  ActivityTableCustomizationService,
  ColumnDataInterface,
  CrmFieldService,
  StandardIds,
  SystemFieldIds,
  TaskCustomColumns,
  LocationCellComponent,
} from '@galaxy/crm/static';
import { TranslateService } from '@ngx-translate/core';
import { TaskNameCellComponent } from '../custom-cells/task-name.component';

const StatusColumnCell = {
  fieldIds: [StandardIds.ActivityTaskStatus, SystemFieldIds.ActivityID],
  columnDefinition: {
    id: StandardIds.ActivityTaskStatus,
  },
  customCellComponent: TaskStatusCellComponent,
};

const BodyColumnCell = {
  fieldIds: [StandardIds.ActivityTaskBody],
  columnDefinition: {
    id: StandardIds.ActivityTaskBody,
  },
  customCellComponent: TaskBodyCellComponent,
};

@Injectable()
export class TaskTableCustomizationService extends ActivityTableCustomizationService implements OnDestroy {
  ownerFilter$$: ReplaySubject<GalaxyFilterDefinitionInterface> = new ReplaySubject<GalaxyFilterDefinitionInterface>(1);
  private readonly shouldShowLocationColumn$$ = new BehaviorSubject<boolean>(false);
  private readonly hasAccessToTaskQueue$ = this.config.hasAccessToFeature$?.('task-queue') ?? of(false);
  private destroy$ = new Subject<void>();
  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    protected override readonly crmFieldService: CrmFieldService,
    private readonly translate: TranslateService,
  ) {
    super(crmFieldService);
    if (this.config.multiLocationContext$) {
      this.config.multiLocationContext$.pipe(takeUntil(this.destroy$)).subscribe((context) => {
        this.shouldShowLocationColumn$$.next(!!context?.groupId);
      });
    }
  }

  defaultSortingOptions: SortByInterface[] = [
    {
      fieldId: this.crmFieldService.getFieldId(StandardIds.ActivityTaskDueDate),
      sortDirection: SortDirection.SORT_DIRECTION_ASCENDING,
    },
  ];

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  customizeColumns(columns: GalaxyColumnDef[], isMobile: boolean): GalaxyColumnDef[] {
    columns = this.excludeNonTaskColumns(columns);
    const customCells = this.config.task?.tableCustomCells || [];
    customCells.push(StatusColumnCell, BodyColumnCell);
    columns = this.addAssociatedCompanyColumn(columns);
    columns = this.addAssociatedContactColumn(columns);
    const shouldShowLocation = this.shouldShowLocationColumn$$.getValue();
    if (shouldShowLocation) {
      columns = this.addLocationColumn(columns);
    }
    columns = this.addCustomColumns(
      customCells.map((c) => c.columnDefinition),
      columns,
    );
    columns = this.showBaseColumns(columns);
    columns = this.sortColumns(columns);
    return [...columns];
  }

  private addLocationColumn(columns: GalaxyColumnDef[]): GalaxyColumnDef[] {
    return [
      {
        id: TaskCustomColumns.Location,
        title: 'Location',
      },
      ...columns,
    ];
  }

  customizeFilters(filters: GalaxyFilterDefinitionInterface[]): Observable<GalaxyFilterDefinitionInterface[]> {
    filters = this.excludeNonTaskFilters(filters);
    this.captureInitialFilters(filters);
    const overrides$ = this.config.task?.filterInputOverrides$ ?? of([]);
    return overrides$.pipe(map((overrides) => this.overrideCustomFilters(filters, overrides)));
  }

  private captureInitialFilters(filters: GalaxyFilterDefinitionInterface[]) {
    const ownerFilterDefinition = filters.find(
      (f) => f.fieldId === this.crmFieldService.getFieldId(SystemFieldIds.ActivityOwnerID),
    );
    if (ownerFilterDefinition) {
      this.ownerFilter$$.next(ownerFilterDefinition);
    }
  }

  async getInitialFilters(): Promise<GalaxyFilterInterface[]> {
    return firstValueFrom(combineLatest([this.ownerFilter$$, this.config.currentUserId$])).then(
      ([filterDefinition, userId]) => {
        return [
          {
            fieldId: filterDefinition.fieldId,
            filterId: 'filterTasksByUser',
            operator: GalaxyFilterOperator.FILTER_OPERATOR_IS,
            values: [
              {
                string: userId,
              },
            ],
          },
          {
            fieldId: this.crmFieldService.getFieldId(StandardIds.ActivityTaskStatus),
            filterId: 'filterTasksByOpenStatus',
            operator: GalaxyFilterOperator.FILTER_OPERATOR_IS,
            values: [
              {
                string: ActivityTaskStatus.Open,
              },
            ],
          },
        ];
      },
    );
  }

  customizeRowData(
    crmObject: CrmObjectInterface,
    schemaMap: { [schemaId: string]: FieldSchemaInterface },
    columnData: ColumnDataInterface,
  ) {
    this.customizeDueDateCellData(columnData);
    this.customizeTaskNameCellData(columnData);
    this.addLocationCustomCell(crmObject, columnData);
    this.addCustomCells(this.config.task?.tableCustomCells || [], crmObject, columnData);
    this.addAssociatedCompanyCellData(crmObject, columnData);
    this.addAssociatedContactCellData(crmObject, columnData);
  }

  private customizeDueDateCellData(columnData: ColumnDataInterface): void {
    const cell = columnData[this.crmFieldService.getFieldId(StandardIds.ActivityTaskDueDate)];
    if (cell) {
      cell.cellType = 'timestamp';
    }
  }

  private addAssociatedCompanyCellData(activity: ActivityInterface, columnData: ColumnDataInterface): void {
    let companyId = '';
    if (activity?.associations) {
      for (const asc of activity.associations) {
        if (asc.crmObjectType === 'Company' && asc.crmObjectId) {
          companyId = asc.crmObjectId;
          break;
        }
      }
    }

    columnData[TaskCustomColumns.AssociatedCompany] = {
      cellType: 'custom',
      value: {
        objectId: companyId,
        objectType: 'Company',
      },
      component: AssociatedCrmObjectComponent,
    } as CustomCell;
  }

  private addAssociatedCompanyColumn(columns: GalaxyColumnDef[]): GalaxyColumnDef[] {
    return [
      {
        id: TaskCustomColumns.AssociatedCompany,
        title: this.translate.instant('TASK.LIST_OBJECTS_TABLE.CUSTOM_COLUMNS.ASSOCIATED_COMPANY'),
      },
      ...columns,
    ];
  }

  private addAssociatedContactCellData(activity: ActivityInterface, columnData: ColumnDataInterface): void {
    let contactId = '';
    if (activity?.associations) {
      for (const asc of activity.associations) {
        if (asc.crmObjectType === 'Contact' && asc.crmObjectId) {
          contactId = asc.crmObjectId;
          break;
        }
      }
    }

    columnData[TaskCustomColumns.AssociatedContact] = {
      cellType: 'custom',
      value: {
        objectId: contactId,
        objectType: 'Contact',
      },
      component: AssociatedCrmObjectComponent,
    } as CustomCell;
  }

  private addAssociatedContactColumn(columns: GalaxyColumnDef[]): GalaxyColumnDef[] {
    return [
      {
        id: TaskCustomColumns.AssociatedContact,
        title: this.translate.instant('TASK.LIST_OBJECTS_TABLE.CUSTOM_COLUMNS.ASSOCIATED_CONTACT'),
      },
      ...columns,
    ];
  }

  private customizeTaskNameCellData(columnData: ColumnDataInterface): void {
    columnData[this.crmFieldService.getFieldId(StandardIds.ActivityName)] = {
      cellType: 'custom',
      value: {
        name: columnData[this.crmFieldService.getFieldId(StandardIds.ActivityName)]?.value,
        id: columnData[this.crmFieldService.getFieldId(SystemFieldIds.ActivityID)]?.value,
      },
      component: TaskNameCellComponent,
    } as CustomCell;
  }

  private addLocationCustomCell(crmObject: ActivityInterface, columnData: ColumnDataInterface): void {
    if (!crmObject.namespace) {
      return;
    }
    columnData[TaskCustomColumns.Location] = {
      cellType: 'custom',
      value: {
        objectId: crmObject.crmObjectId,
        objectType: 'Task',
        namespace: crmObject.namespace,
      },
      component: LocationCellComponent,
    } as CustomCell;
  }

  private excludeNonTaskColumns(columns: GalaxyColumnDef[]) {
    return columns.filter((c) => this.getTaskFieldIds().includes(c.id));
  }

  private excludeNonTaskFilters(filters: GalaxyFilterDefinitionInterface[]) {
    return filters.filter((f) => f.fieldId && this.getTaskFieldIds().includes(f.fieldId));
  }

  private getTaskFieldIds(): string[] {
    const fieldIds = [
      this.crmFieldService.getFieldId(SystemFieldIds.ActivityOwnerID),
      this.crmFieldService.getFieldId(SystemFieldIds.ActivityID),
      this.crmFieldService.getFieldId(StandardIds.ActivityName),
      this.crmFieldService.getFieldId(StandardIds.ActivityTaskDueDate),
      this.crmFieldService.getFieldId(StandardIds.ActivityTaskPriority),
      this.crmFieldService.getFieldId(StandardIds.ActivityTaskStatus),
      this.crmFieldService.getFieldId(StandardIds.ActivityTaskType),
      this.crmFieldService.getFieldId(StandardIds.ActivityTaskBody),
      this.crmFieldService.getFieldId(SystemFieldIds.ActivityCreated),
      this.crmFieldService.getFieldId(SystemFieldIds.ActivityUpdated),
    ];
    const shouldShowLocation = this.shouldShowLocationColumn$$.getValue();
    if (shouldShowLocation) {
      fieldIds.push(TaskCustomColumns.Location);
    }
    return fieldIds;
  }

  private showBaseColumns(columns: GalaxyColumnDef[]): GalaxyColumnDef[] {
    const columnsToHide = [
      SystemFieldIds.ActivityID,
      SystemFieldIds.ActivityOwnerID,
      SystemFieldIds.ActivityCreated,
      SystemFieldIds.ActivityUpdated,
      StandardIds.ActivityTaskPriority,
    ];

    const columnsToUnHide = [TaskCustomColumns.AssignedTo];

    for (const column of columns) {
      if (columnsToHide.includes(column.id)) {
        column.hidden = true;
      } else if (columnsToUnHide.includes(column.id)) {
        column.hidden = false;
      }
    }

    return columns;
  }

  private sortColumns(columns: GalaxyColumnDef[]): GalaxyColumnDef[] {
    const columnOrder = [
      StandardIds.ActivityTaskStatus,
      StandardIds.ActivityName,
      StandardIds.ActivityTaskBody,
      StandardIds.ActivityTaskType,
      StandardIds.ActivityTaskDueDate,
      TaskCustomColumns.AssignedTo,
    ];

    return columns.sort((a, b) => {
      const order = columnOrder;
      const aIndex = order.indexOf(a.id);
      const bIndex = order.indexOf(b.id);
      return (aIndex === -1 ? Infinity : aIndex) - (bIndex === -1 ? Infinity : bIndex);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
